import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import ListCoverImage from './Common/ListCoverImage';

/**
 * Shared mobile list layout primitives for book-style grids (cover image + title).
 * Used by mBooksListPage, magazines, kalyans, audio lists, and other m*ListPage files.
 * Article lists use mArticlesPage.tsx instead.
 */

interface ViewProps {
    isGridView: boolean;
}

export const HeaderBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isSearchActive',
})<{ isSearchActive?: boolean }>(({ isSearchActive }) => ({
    width: '100%',
    backgroundColor: '#f97316',
    height: '90px',
    flexShrink: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily:"chanakyaUni",
    justifyContent: 'center',
    ...(isSearchActive && {
        justifyContent: 'center',
        alignItems: 'center',
    }),
}));

export const MainContentBox = styled(Box)(() => ({
    flexGrow: 1,
    overflowY: 'auto',
    paddingBottom: '60px',
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
}));

export const BookGridContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'isGridView',
})<ViewProps>(({ theme, isGridView }) => ({
    padding: theme.spacing(1),
    justifyContent: 'flex-start',
    flexDirection: isGridView ? 'row' : 'column',
}));

export const BookItemBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isGridView',
})<ViewProps>(({ theme, isGridView }) => ({
    backgroundColor: 'white',
    padding: isGridView ? '3px 3px 8px' : theme.spacing(1),
    textAlign: isGridView ? 'center' : 'left',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: isGridView ? 'column' : 'row',
    height: isGridView ? '100%' : 'auto',
    borderRadius: isGridView ? '8px 8px 0 0' : theme.shape.borderRadius,
    overflow: 'hidden',
    alignItems: isGridView ? 'stretch' : 'center',
    gap: isGridView ? 0 : theme.spacing(1.5),
    marginBottom: isGridView ? 0 : theme.spacing(1),
    cursor: 'pointer',
}));

type BookImageProps = ViewProps & {
    src?: string;
    alt?: string;
    className?: string;
    style?: React.CSSProperties;
    imgStyle?: React.CSSProperties;
    onLoad?: React.ReactEventHandler<HTMLImageElement>;
    onError?: React.ReactEventHandler<HTMLImageElement>;
};

export const BookImage = ({
    isGridView,
    src,
    alt,
    className,
    style,
    imgStyle,
    onLoad,
    onError,
}: BookImageProps) => {
    const frameStyle: React.CSSProperties = {
        width: isGridView ? '100%' : '60px',
        height: isGridView ? 'auto' : '80px',
        aspectRatio: isGridView ? '3 / 4' : undefined,
        marginBottom: isGridView ? '6px' : '0',
        borderRadius: isGridView ? '6px 6px 0 0' : undefined,
        overflow: isGridView ? 'hidden' : undefined,
        flexShrink: 0,
        ...style,
    };

    return (
        <ListCoverImage
            className={className}
            src={src}
            alt={alt}
            style={frameStyle}
            imgStyle={{
                objectFit: isGridView ? 'cover' : 'contain',
                ...imgStyle,
            }}
            onLoad={onLoad}
            onError={onError}
        />
    );
};

export const BookTitleTypography = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isGridView',
})<ViewProps>(({ isGridView }) => ({
    fontWeight: 'bold',
    color: '#4b2e2e',
    fontSize: isGridView ? '12px' : '14px',
    padding: isGridView ? '0 4px' : 0,
    display: '-webkit-box',
    WebkitLineClamp: isGridView ? 2 : 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    flexGrow: 1,
}));

export const FixedBottomBar = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '40px',
    backgroundColor: '#f97316',
    position: 'fixed',
    bottom: 0,
    left: 0,
    padding: theme.spacing(1),
}));

export const SkeletonBookGridContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'isGridView',
})<ViewProps>(({ theme, isGridView }) => ({
    padding: theme.spacing(1),
    justifyContent: isGridView ? 'center' : 'flex-start',
    flexDirection: isGridView ? 'row' : 'column',
}));

export const SkeletonBookItemBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isGridView',
})<ViewProps>(({ theme, isGridView }) => ({
    backgroundColor: 'white',
    padding: isGridView ? '3px 3px 8px' : theme.spacing(1),
    textAlign: isGridView ? 'center' : 'left',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: isGridView ? 'column' : 'row',
    height: isGridView ? '100%' : 'auto',
    borderRadius: isGridView ? '8px 8px 0 0' : theme.shape.borderRadius,
    overflow: 'hidden',
    alignItems: isGridView ? 'stretch' : 'center',
    gap: isGridView ? 0 : theme.spacing(1.5),
    marginBottom: isGridView ? 0 : theme.spacing(1),
}));
